import { useState } from "react";
import type { OrderType, Place } from "../api";
import { money, Sheet } from "../ui";

/**
 * Оформление (п. 7 ТЗ).
 *
 * Гость не регистрируется и телефон не подтверждает. Спрашивается ровно то,
 * без чего заказ не выполнить: как обратиться, куда позвонить и что учесть.
 *
 * Способы получения показываются те, что заведение действительно принимает.
 * Предзаказ ко времени, бронь столика и доставка появятся на следующих
 * этапах — пока они видны как «скоро», чтобы гость знал, что они будут, и
 * не искал их в другом приложении.
 */

const SCENARIOS: {
  id: OrderType;
  key: "pickup" | "preorder" | "booking" | "delivery";
  title: string;
  note: string;
  ready: boolean;
}[] = [
  {
    id: "pickup",
    key: "pickup",
    title: "Забрать сейчас",
    note: "Приготовят как можно скорее",
    ready: true
  },
  {
    id: "preorder",
    key: "preorder",
    title: "Ко времени",
    note: "Скоро",
    ready: false
  },
  {
    id: "dine_in",
    key: "booking",
    title: "За столик",
    note: "Скоро",
    ready: false
  },
  {
    id: "delivery",
    key: "delivery",
    title: "Доставка",
    note: "Скоро",
    ready: false
  }
];

export function Checkout({
  place,
  total,
  busy,
  error,
  onClose,
  onSubmit
}: {
  place: Place | null;
  total: number;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: {
    type: OrderType;
    customerName: string;
    customerPhone: string;
    comment: string;
  }) => void;
}) {
  const [type, setType] = useState<OrderType>("pickup");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");

  // Одиннадцать цифр российского номера. Проверка есть и на сервере, но
  // узнать об опечатке до нажатия «оплатить» лучше, чем после.
  const digits = phone.replace(/\D/g, "");
  const phoneOk = digits.length === 11;

  return (
    <Sheet
      title="Оформление"
      note={place?.name ?? ""}
      onClose={onClose}
      footer={
        <>
          {error ? <p className="notice" style={{ marginBottom: 12 }}>{error}</p> : null}

          <button
            type="button"
            className="btn"
            disabled={busy || !phoneOk}
            onClick={() =>
              onSubmit({ type, customerName: name, customerPhone: phone, comment })
            }
          >
            <span>{busy ? "Отправляем…" : "Оплатить"}</span>
            <span>{money(total)}</span>
          </button>
        </>
      }
    >
      <div className="choices">
        {SCENARIOS.map(scenario => {
          const allowed = scenario.ready && (place ? place.accepts[scenario.key] : scenario.ready);

          return (
            <button
              key={scenario.id}
              type="button"
              className="choice"
              aria-pressed={type === scenario.id}
              disabled={!allowed}
              onClick={() => setType(scenario.id)}
            >
              <strong>{scenario.title}</strong>
              <span>{scenario.note}</span>
            </button>
          );
        })}
      </div>

      {place?.address ? (
        <p className="muted small" style={{ marginTop: 12 }}>
          Забрать: {place.address}
        </p>
      ) : null}

      <label className="field">
        <span>Как к вам обратиться</span>
        <input
          value={name}
          onChange={event => setName(event.target.value)}
          placeholder="Имя"
          autoComplete="given-name"
          maxLength={60}
        />
      </label>

      <label className="field">
        <span>Телефон</span>
        <input
          value={phone}
          onChange={event => setPhone(event.target.value)}
          placeholder="+7 900 000-00-00"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          maxLength={20}
        />
      </label>

      <label className="field">
        <span>Комментарий</span>
        <textarea
          value={comment}
          onChange={event => setComment(event.target.value)}
          placeholder="Что учесть при готовке"
          rows={3}
          maxLength={400}
        />
      </label>

      <p className="muted small" style={{ marginTop: 16, marginBottom: 20 }}>
        Нажимая «Оплатить», вы соглашаетесь с условиями обработки персональных
        данных.
      </p>
    </Sheet>
  );
}
